import { z } from 'zod';
import { CREDIT_BUREAUS, type CreditBureau } from './services.js';

// ---------------------------------------------------------------------------
// Credit monitoring (Credit Building clients, including ITIN holders)
//
// Scores are only ever shown when they come from an authorized source (a client-authorized report reviewed by staff,
// or an approved data provider). A missing score is stored as NULL and shown as "No Score Available" — never 0.
// Scores are compared only within the same bureau AND the same scoring model (VantageScore 3.0 ≠ FICO 8).
// ---------------------------------------------------------------------------

/** Scoring models we record. "Other" requires a free-text model name. Different models are never compared. */
export const CREDIT_SCORE_MODELS = [
  'VantageScore 3.0',
  'VantageScore 4.0',
  'FICO 8',
  'FICO 9',
  'FICO 10',
  'FICO 10T',
  'FICO 2',
  'FICO 4',
  'FICO 5',
  'FICO Auto 8',
  'FICO Bankcard 8',
  'Other',
] as const;
export type CreditScoreModel = (typeof CREDIT_SCORE_MODELS)[number];

/** Score ranges per family (validation only; never used to invent scores). */
export function scoreRange(model: string): [number, number] {
  if (/^FICO (Auto|Bankcard)/.test(model)) return [250, 900];
  return [300, 850];
}

export const CREDIT_REPORT_SOURCES = ['manual_upload', 'myfreescorenow', 'provider'] as const;
export type CreditReportSource = (typeof CREDIT_REPORT_SOURCES)[number];
export const CREDIT_REPORT_SOURCE_LABELS: Record<CreditReportSource, string> = {
  manual_upload: 'Client-authorized report upload (manual review)',
  myfreescorenow: 'MyFreeScoreNow',
  provider: 'Credit data provider',
};

/** A report covers one bureau or all three (tri-merge / 3-bureau monitoring export). */
export const CREDIT_REPORT_BUREAUS = ['experian', 'equifax', 'transunion', 'tri_merge'] as const;
export type CreditReportBureau = (typeof CREDIT_REPORT_BUREAUS)[number];
export const CREDIT_REPORT_BUREAU_LABELS: Record<CreditReportBureau, string> = {
  experian: 'Experian',
  equifax: 'Equifax',
  transunion: 'TransUnion',
  tri_merge: 'All three bureaus',
};

export const CREDIT_REVIEW_STATUSES = ['pending_review', 'reviewed', 'rejected'] as const;
export type CreditReviewStatus = (typeof CREDIT_REVIEW_STATUSES)[number];

export const CREDIT_CONSENT_TYPES = ['report_upload_review', 'provider_pull', 'mfsn_enrollment'] as const;
export type CreditConsentType = (typeof CREDIT_CONSENT_TYPES)[number];
export const CREDIT_CONSENT_LABELS: Record<CreditConsentType, string> = {
  report_upload_review: 'Review of credit reports the client provides',
  provider_pull: 'Retrieval of credit data from an approved provider',
  mfsn_enrollment: 'MyFreeScoreNow enrollment and report sharing',
};
export const CREDIT_CONSENT_METHODS = ['portal_esign', 'signed_form'] as const;
export type CreditConsentMethod = (typeof CREDIT_CONSENT_METHODS)[number];
/** FCRA §604(a)(2): the consumer's written instructions. */
export const CREDIT_PERMISSIBLE_PURPOSE = 'written_instructions_604a2';
/** The wording the client agrees to (stored verbatim with each consent). */
export const CREDIT_CONSENT_TEXT: Record<CreditConsentType, string> = {
  report_upload_review:
    'I authorize Foreign Founders (Everest Ventures Group LLC) to receive, store and review the credit reports I provide, to track my credit-building progress. I can revoke this authorization at any time; my reports are then deleted according to the retention policy.',
  provider_pull:
    'I give my written instructions under the Fair Credit Reporting Act authorizing Foreign Founders (Everest Ventures Group LLC) to obtain my consumer credit report and credit scores from its approved credit data provider, periodically while I am enrolled, to monitor my credit-building progress. I can revoke this authorization at any time.',
  mfsn_enrollment:
    'I authorize Foreign Founders (Everest Ventures Group LLC) to receive the credit reports and scores from my MyFreeScoreNow monitoring account that I choose to share, to monitor my credit-building progress. Foreign Founders will never ask for or store my MyFreeScoreNow password. I can revoke this authorization at any time.',
};

export const CREDIT_EVENT_TYPES = [
  'score_change',
  'new_account',
  'authorized_user_reporting',
  'secured_card_reporting',
  'secured_loan_reporting',
  'hard_inquiry',
  'limit_change',
  'late_payment',
  'utilization_change',
  'discrepancy',
  'no_score_yet',
  'first_score',
] as const;
export type CreditEventType = (typeof CREDIT_EVENT_TYPES)[number];
export const CREDIT_EVENT_LABELS: Record<CreditEventType, string> = {
  score_change: 'Credit score change',
  new_account: 'New account reporting',
  authorized_user_reporting: 'Authorized-user account reporting',
  secured_card_reporting: 'Secured card reporting',
  secured_loan_reporting: 'Secured loan reporting',
  hard_inquiry: 'New hard inquiry',
  limit_change: 'Credit limit change',
  late_payment: 'Late-payment indicator',
  utilization_change: 'Utilization change',
  discrepancy: 'Reporting discrepancy',
  no_score_yet: 'No score generated yet',
  first_score: 'First score available',
};
export const CREDIT_EVENT_SEVERITIES = ['info', 'review', 'critical'] as const;
export type CreditEventSeverity = (typeof CREDIT_EVENT_SEVERITIES)[number];

/** Which roadmap phase each event belongs to (Credit Building template phase numbers). */
export const CREDIT_EVENT_PHASE: Record<CreditEventType, number> = {
  score_change: 2,
  new_account: 4,
  authorized_user_reporting: 2,
  secured_card_reporting: 3,
  secured_loan_reporting: 3,
  hard_inquiry: 4,
  limit_change: 6,
  late_payment: 6,
  utilization_change: 6,
  discrepancy: 2,
  no_score_yet: 1,
  first_score: 2,
};

export const MFSN_STATUSES = ['invited', 'enrolled', 'active', 'cancelled', 'failed'] as const;
export type MfsnStatus = (typeof MFSN_STATUSES)[number];
export const MFSN_STATUS_LABELS: Record<MfsnStatus, string> = {
  invited: 'Invited',
  enrolled: 'Enrolled',
  active: 'Active — sharing reports',
  cancelled: 'Cancelled',
  failed: 'Enrollment failed',
};

export interface CreditMonitoringSettings {
  /** Days between scheduled credit reviews. */
  reviewCadenceDays: number;
  /** Months a stored report file is kept before deletion (observations stay unless the client asks for deletion). */
  retentionMonths: number;
  /** Utilization (%) above which a review event is raised. */
  utilizationAlertPct: number;
  /** Utilization change (percentage points) that is worth a review. */
  utilizationChangePts: number;
  /** Score drop (points, same bureau + model) that is critical. */
  scoreDropCritical: number;
  /** Score change (points) worth an informational event. */
  scoreChangeNotable: number;
  /** Hours for Arju to review an event (critical events: half). */
  reviewHours: number;
  /** Months consent records are kept after revocation (FCRA practice: 5 years). */
  consentRetentionMonths: number;
  /** When the data provider does not allow redisplaying raw scores to the consumer, the portal shows trends only. */
  portalShowScores: boolean;
}
export const DEFAULT_CREDIT_SETTINGS: CreditMonitoringSettings = {
  reviewCadenceDays: 30,
  retentionMonths: 24,
  utilizationAlertPct: 30,
  utilizationChangePts: 10,
  scoreDropCritical: 25,
  scoreChangeNotable: 10,
  reviewHours: 48,
  consentRetentionMonths: 60,
  portalShowScores: true,
};

// ---------- Inputs ----------
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const pct = z.number().min(0).max(1000).nullish();
const count = z.number().int().min(0).max(10_000).nullish();
const cents = z.number().int().min(0).max(100_000_000_000).nullish();

export const creditConsentInput = z.object({
  consentType: z.enum(CREDIT_CONSENT_TYPES),
  method: z.enum(CREDIT_CONSENT_METHODS).default('signed_form'),
  signedAt: dateOnly.optional(),
  signerName: z.string().trim().min(2).max(200),
  enrollmentId: z.string().uuid().nullish(),
  notes: z.string().trim().max(2000).nullish(),
});

/** One bureau's figures read from a report by staff. `score: null` with `noScore: true` = the report has no score. */
export const bureauReadingInput = z
  .object({
    bureau: z.enum(CREDIT_BUREAUS),
    noScore: z.boolean().default(false),
    score: z.number().int().min(250).max(900).nullish(),
    scoreModel: z.enum(CREDIT_SCORE_MODELS).nullish(),
    scoreModelOther: z.string().trim().max(60).nullish(),
    utilizationPct: pct,
    totalLimitsCents: cents,
    totalBalancesCents: cents,
    openAccounts: count,
    creditAgeMonths: count,
    onTimePct: pct,
    latePayments30: count,
    latePayments60: count,
    latePayments90: count,
    derogatories: count,
    hardInquiries12m: count,
    softInquiries: count,
    collections: count,
    publicRecords: count,
    securedCardReporting: z.boolean().default(false),
    securedLoanReporting: z.boolean().default(false),
    authorizedUserAccounts: count,
    frozen: z.boolean().default(false),
    fraudAlert: z.boolean().default(false),
    discrepancies: z.array(z.string().trim().min(1).max(300)).max(20).default([]),
  })
  .superRefine((b, ctx) => {
    if (b.noScore) {
      if (b.score != null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['score'], message: 'Leave the score empty when the report has no score' });
      return;
    }
    if (b.score == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['score'], message: 'Enter the score, or tick "No score on this report"' });
      return;
    }
    if (!b.scoreModel) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scoreModel'], message: 'The scoring model is required with a score' });
    if (b.scoreModel === 'Other' && !b.scoreModelOther) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scoreModelOther'], message: 'Name the scoring model' });
    const model = b.scoreModel === 'Other' ? (b.scoreModelOther ?? '') : (b.scoreModel ?? '');
    const [lo, hi] = scoreRange(model);
    if (b.score < lo || b.score > hi) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['score'], message: `${model} scores range ${lo}–${hi}` });
  });
export type BureauReadingInput = z.infer<typeof bureauReadingInput>;

export const tradelineInput = z.object({
  bureau: z.enum(CREDIT_BUREAUS),
  creditor: z.string().trim().min(1).max(120),
  accountType: z.enum(['credit_card', 'secured_card', 'secured_loan', 'installment_loan', 'auto_loan', 'mortgage', 'student_loan', 'other']),
  isAuthorizedUser: z.boolean().default(false),
  openedOn: dateOnly.nullish(),
  limitCents: cents,
  balanceCents: cents,
  status: z.string().trim().max(60).nullish(),
});

export const creditReportReviewInput = z.object({
  decision: z.enum(['reviewed', 'rejected']),
  reportDate: dateOnly,
  readings: z.array(bureauReadingInput).max(3).default([]),
  tradelines: z.array(tradelineInput).max(60).default([]),
  rejectReason: z.string().trim().max(500).nullish(),
  notes: z.string().trim().max(2000).nullish(),
});

export const creditReportUploadFields = z.object({
  bureau: z.enum(CREDIT_REPORT_BUREAUS),
  source: z.enum(['manual_upload', 'myfreescorenow']).default('manual_upload'),
  reportDate: dateOnly.optional(),
  enrollmentId: z.string().uuid().optional(),
});

export const creditEventResolveInput = z.object({ note: z.string().trim().min(1).max(1000) });

export const mfsnUpdateInput = z.object({
  status: z.enum(MFSN_STATUSES),
  providerMemberRef: z
    .string()
    .trim()
    .max(80)
    .regex(/^[\w\-.]*$/, 'Member reference only (letters, numbers, - and .) — never a password')
    .nullish(),
  notes: z.string().trim().max(1000).nullish(),
});

export const creditSettingsInput = z.object({
  reviewCadenceDays: z.number().int().min(7).max(365),
  retentionMonths: z.number().int().min(1).max(84),
  utilizationAlertPct: z.number().min(1).max(100),
  utilizationChangePts: z.number().min(1).max(100),
  scoreDropCritical: z.number().int().min(1).max(300),
  scoreChangeNotable: z.number().int().min(1).max(300),
  reviewHours: z.number().int().min(4).max(336),
  consentRetentionMonths: z.number().int().min(12).max(120),
  portalShowScores: z.boolean(),
});

/** The display label of a model reading ("Other" readings carry their own name). */
export const modelLabel = (model: string | null | undefined) => model ?? '—';
export const bureauLabel = (b: CreditBureau | CreditReportBureau) => ({ experian: 'Experian', equifax: 'Equifax', transunion: 'TransUnion', tri_merge: 'All three bureaus' })[b];
